import SearchIcon from '@mui/icons-material/Search';
import { 
    Alert, 
    Backdrop, 
    Box, 
    Button,
    Chip,
    CircularProgress, 
    FormControlLabel, 
    InputAdornment, 
    MenuItem, 
    Pagination, 
    Paper, 
    Snackbar, 
    Switch, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TextField,
    Tooltip,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Divider,
    IconButton,
    Stack
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { StyledTableRow } from '../../../../../Utils/styledcell';
import AdminWithdrawalDialog from './adminWithdrawalDialog';
import RetryWithdrawalDialog from './retryWithdrawalDialog';
import ProcessAutomatedDialog from './ProcessAutomatedDialog';
import CreatorPaymentDialog from '../../helpers/creatortaxwalletinfo';
import { useMutation, useQuery } from 'react-query';
import API from '../../../../../API';
import AlertDialog from '../../../../../Components/AlertDialog';
import useAlertDialog from '../../../../../Components/useAlertDialog';
import FeePayerIcon from '../../../../../Components/payments/FeePayerIcon';
import { withdrawalStatuses } from '../../../../../Utils/constants';
import { 
    PlayArrow as ProcessIcon,
    Settings as SettingsIcon,
    Assessment as StatsIcon,
    Refresh as RefreshIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    AutoMode as AutomatedIcon,
    Handyman as ManualIcon,
    AccountBalanceWallet as PaymentInfoIcon,
    Person as PersonIcon,
    Replay as RetryIcon,
    Lock as LockIcon
} from '@mui/icons-material';
import { toast } from 'sonner';

const WithdrawalToSubjectData = (wdw) => {
    switch (wdw.entity_type) {
        case 'company':
            return {
                subject: wdw.company.name,
                subjectPaymentMethod: 'N/A',
                subjectPaymentEmail: 'N/A',
                entityType: 'company',
                entityId: wdw.company_id
            }
            break;
        case 'creator':
            // Handle orphaned withdrawals with null creator
            if (!wdw.creator) {
                return {
                    subject: 'Unknown Creator',
                    subjectPaymentMethod: 'N/A',
                    subjectPaymentEmail: 'N/A',
                    entityType: 'creator',
                    entityId: wdw.creator_id || 'N/A',
                    creatorId: null
                };
            }

            let subjectPaymentEmail = wdw.creator.payout_email
            let subjectPaymentMethod = 'paypal'

            // Check withdrawal's actual payment_service first (set during processing)
            if (wdw.payment_service) {
                subjectPaymentMethod = wdw.payment_service;
            } else if (wdw.creator.user) {
                // Fallback to legacy creator preference
                if (wdw.creator.user.payout_preferred == 'PayPal') {
                    subjectPaymentMethod = 'paypal'
                    subjectPaymentEmail = wdw.creator.user.paypal
                }
                if (wdw.creator.user.payout_preferred == 'Stripe') {
                    subjectPaymentMethod = 'stripe'
                    subjectPaymentEmail = wdw.creator.user.stripe_id
                }
            }

            return {
                subject: wdw.creator.creator,
                subjectPaymentMethod,
                subjectPaymentEmail,
                entityType: 'creator',
                entityId: wdw.creator_id,
                creatorId: wdw.creator.creator // creator username for API calls
            }
        case 'user':
            return {
                subject: wdw.global_user?.username || wdw.global_user?.email || 'Unknown User',
                subjectPaymentMethod: wdw.payment_service || 'N/A',
                subjectPaymentEmail: wdw.recipient_email || wdw.global_user?.email || 'N/A',
                entityType: 'user',
                entityId: wdw.user_id
            };
        default:
            return {
                subject: 'Unknown',
                subjectPaymentMethod: 'N/A',
                subjectPaymentEmail: 'N/A',
                entityType: wdw.entity_type || 'unknown',
                entityId: 'N/A'
            };
    }
}

// New component for processing status indicator
const ProcessingStatusIndicator = ({ withdrawal }) => {
    if (!withdrawal.processing_mode) return null;

    const isAutomated = withdrawal.processing_mode === 'automated';
    const isManualFallback = withdrawal.processing_mode === 'manual_fallback';
    const isProcessing = withdrawal.status === 'processing';
    const hasBalanceDeducted = withdrawal.balance_deducted_at;
    const processingAttempts = withdrawal.automated_processing_attempts || 0;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
                icon={isAutomated ? <AutomatedIcon /> : isManualFallback ? <WarningIcon /> : <ManualIcon />}
                label={isAutomated ? 'Automated' : isManualFallback ? 'Manual Fallback' : 'Manual'}
                size="small"
                color={isAutomated ? 'primary' : isManualFallback ? 'warning' : 'default'}
                variant="outlined"
            />
            {isAutomated && (
                <>
                    {hasBalanceDeducted && (
                        <Chip
                            icon={<SuccessIcon />}
                            label="Balance Deducted"
                            size="small"
                            color="success"
                            variant="outlined"
                        />
                    )}
                    {processingAttempts > 0 && (
                        <Chip
                            label={`${processingAttempts} attempts`}
                            size="small"
                            color={processingAttempts > 3 ? 'error' : 'warning'}
                            variant="outlined"
                        />
                    )}
                </>
            )}
        </Box>
    );
};

// New component for action buttons
const WithdrawalActions = ({ withdrawal, onProcessAutomated, onConvertMode, onRefresh, onRetry, onProcessApprovedManual }) => {
    const isAutomated = withdrawal.processing_mode === 'automated';
    const canProcessAutomated = withdrawal.status === 'approved' && isAutomated;
    const canConvert = withdrawal.status === 'pending' || withdrawal.status === 'approved';
    const canRetry = withdrawal.status === 'failed' && isAutomated; // Admin retries are unlimited
    const canProcessApprovedManual = ['approved', 'pending'].includes(withdrawal.status) && !isAutomated;

    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canProcessAutomated && (
                <Tooltip title="Process automated withdrawal">
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<ProcessIcon />}
                        onClick={() => onProcessAutomated(withdrawal.id)}
                    >
                        Process
                    </Button>
                </Tooltip>
            )}

            {canProcessApprovedManual && (
                <Tooltip title="Convert to automated processing">
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<ProcessIcon />}
                        onClick={() => onProcessApprovedManual(withdrawal)}
                    >
                        Process Automated
                    </Button>
                </Tooltip>
            )}

            {canRetry && (
                <Tooltip title="Retry failed withdrawal">
                    <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        startIcon={<RetryIcon />}
                        onClick={() => onRetry(withdrawal)}
                    >
                        Retry
                    </Button>
                </Tooltip>
            )}
            
            {canConvert && (
                <Tooltip title={`Convert to ${isAutomated ? 'manual' : 'automated'} mode`}>
                    <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={isAutomated ? <ManualIcon /> : <AutomatedIcon />}
                        onClick={() => onConvertMode(withdrawal, isAutomated ? 'manual' : 'automated')}
                    >
                        {isAutomated ? 'Manual' : 'Auto'}
                    </Button>
                </Tooltip>
            )}
            
            <Tooltip title="Refresh status">
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => onRefresh(withdrawal.id)}
                >
                    Refresh
                </Button>
            </Tooltip>
        </Box>
    );
};

// Enhanced component for creator info with payment access
const CreatorInfoDisplay = ({ subjectData, onPaymentInfoClick }) => {
    if (subjectData.entityType !== 'creator') {
        return (
            <Box>
                <Typography variant="body2">{subjectData.subject}</Typography>
                <Typography variant="caption" color="textSecondary">
                    {subjectData.entityType}
                </Typography>
            </Box>
        );
    }

    return (
        <Stack spacing={0.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Chip
                    icon={<PersonIcon />}
                    label={subjectData.subject}
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => onPaymentInfoClick(subjectData.creatorId, subjectData.subject)}
                    sx={{ cursor: 'pointer' }}
                />
                <IconButton 
                    size="small" 
                    onClick={() => onPaymentInfoClick(subjectData.creatorId, subjectData.subject)}
                    title="View Payment Info"
                >
                    <PaymentInfoIcon fontSize="small" />
                </IconButton>
            </Stack>
            <Typography variant="caption" color="textSecondary">
                {subjectData.subjectPaymentMethod} • {subjectData.subjectPaymentEmail}
            </Typography>
        </Stack>
    );
};

function WithdrawalRow({ wdw, onClickWithdrawal, onProcessAutomated, onConvertMode, onRefresh, onRetry, onProcessApprovedManual, onPaymentInfoClick }) {
    const subjectData = WithdrawalToSubjectData(wdw);

    // Create a new Date object by copying the start date
    const paidByDate = new Date(wdw.created_at.getTime());
    // Add the specified number of days
    paidByDate.setDate(paidByDate.getDate() + wdw.days_to_payment);
    const paidByDatePassed = paidByDate.getTime() < Date.now() && wdw.status!='paid';

    return (
        <StyledTableRow
            onClick={() => onClickWithdrawal(wdw.id)}
            hover
            sx={theme => ({
                backgroundColor: paidByDatePassed ? theme.palette.error.main : 'rgba(0,0,0,0)',
                color: paidByDatePassed ? theme.palette.error.contrastText : theme.palette.text.primary,
                '&.MuiTableRow-hover:hover': {
                    backgroundColor: paidByDatePassed ? theme.palette.error.light : theme.palette.action.hover,
                },
                '&.MuiTableRow-root:nth-of-type(odd)': {
                    backgroundColor: paidByDatePassed ? theme.palette.error.dark : theme.palette.action.focus,
                },
                '&.MuiTableRow-hover:nth-of-type(odd):hover': {
                    backgroundColor: paidByDatePassed ? theme.palette.error.light : theme.palette.action.hover,
                },

            })}>
            <TableCell sx={{textWrap:'nowrap'}}>
                {wdw.reference}
            </TableCell>
            <TableCell sx={{ textTransform: 'capitalize' }}>
                <Box>
                    <Typography variant="body2">{wdw.status}</Typography>
                    <ProcessingStatusIndicator withdrawal={wdw} />
                    {/* Processing Mode Indicator */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        {wdw.processing_mode === 'automated' ? (
                            <Chip
                                size="small"
                                icon={<AutomatedIcon />}
                                label="Auto"
                                color="primary"
                                variant="outlined"
                            />
                        ) : wdw.processing_mode === 'manual_fallback' ? (
                            <Chip
                                size="small"
                                icon={<WarningIcon />}
                                label="Manual Fallback"
                                color="warning"
                                variant="outlined"
                            />
                        ) : (
                            <Chip
                                size="small"
                                icon={<ManualIcon />}
                                label="Manual"
                                color="default"
                                variant="outlined"
                            />
                        )}
                    </Box>
                    {/* Rail Lock Indicator */}
                    {wdw.external_metadata?.rail_lock?.forced_payment_rail && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <Chip
                                size="small"
                                icon={<LockIcon />}
                                label={
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <span>{`Rail: ${wdw.external_metadata.rail_lock.forced_payment_rail}`}</span>
                                        {(wdw.rail_fee_amount || 0) > 0 && (
                                            <span style={{ opacity: 0.85 }}>{` ($${Number(wdw.rail_fee_amount).toFixed(2)})`}</span>
                                        )}
                                        <FeePayerIcon
                                            payer={wdw.rail_fee_payer ?? wdw.external_metadata.rail_lock.rail_fee_payer}
                                            size={12}
                                        />
                                    </Stack>
                                }
                                color="info"
                                variant="outlined"
                            />
                        </Box>
                    )}
                </Box>
            </TableCell>
            <TableCell>
                {wdw.created_at.toLocaleString()}
            </TableCell>
            <TableCell>
                {paidByDate.toLocaleString()}
            </TableCell>
            <TableCell>
                ${wdw.amount_no_fee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </TableCell>
            <TableCell>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>
                        {Number(wdw.display_fee_percentage ?? wdw.fee_percentage ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}% ($
                        {Number(wdw.display_fee_amount ?? (wdw.amount_no_fee * (wdw.fee_percentage || 0) / 100)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        )
                    </span>
                    <FeePayerIcon payer={wdw.fee_payer} />
                </Stack>
            </TableCell>
            <TableCell>
                ${((wdw.amount_no_fee || 0)
                    - ((wdw.fee_payer || ((wdw.fee_percentage || 0) > 0 ? 'creator' : 'me')) === 'creator' ? Number(wdw.display_fee_amount ?? (wdw.amount_no_fee * (wdw.fee_percentage || 0) / 100)) : 0)
                    - ((wdw.rail_fee_payer ?? wdw.external_metadata?.rail_lock?.rail_fee_payer) === 'creator' ? Number(wdw.rail_fee_amount ?? 0) : 0)
                 ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </TableCell>
            <TableCell>
                {wdw.entity_type}
            </TableCell>
            {/* Enhanced Subject cell with payment info access */}
            <TableCell onClick={(e) => e.stopPropagation()}>
                <CreatorInfoDisplay 
                    subjectData={subjectData}
                    onPaymentInfoClick={onPaymentInfoClick}
                />
            </TableCell>
            <TableCell>
                {subjectData.subjectPaymentMethod}
            </TableCell>
            <TableCell>
                {subjectData.subjectPaymentEmail}
            </TableCell>
            <TableCell>
                {wdw.brief}
            </TableCell>
            <TableCell>
                {wdw.notes}
            </TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
                <WithdrawalActions
                    withdrawal={wdw}
                    onProcessAutomated={onProcessAutomated}
                    onConvertMode={onConvertMode}
                    onRefresh={onRefresh}
                    onRetry={onRetry}
                    onProcessApprovedManual={onProcessApprovedManual}
                />
            </TableCell>
        </StyledTableRow>
    )
}

// New component for batch processing dialog
const BatchProcessingDialog = ({ open, onClose, onProcess }) => {
    const [batchSize, setBatchSize] = useState(10);
    const [dryRun, setDryRun] = useState(true);
    const [forceProcessing, setForceProcessing] = useState(false);
    
    const handleProcess = () => {
        onProcess({
            max_batch_size: batchSize,
            dry_run: dryRun,
            force_processing: forceProcessing
        });
        onClose();
    };
    
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Batch Process Automated Withdrawals</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Batch Size"
                            type="number"
                            value={batchSize}
                            onChange={(e) => setBatchSize(parseInt(e.target.value) || 10)}
                            inputProps={{ min: 1, max: 50 }}
                            helperText="Maximum number of withdrawals to process"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={dryRun}
                                    onChange={(e) => setDryRun(e.target.checked)}
                                />
                            }
                            label="Dry Run (validate only)"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={forceProcessing}
                                    onChange={(e) => setForceProcessing(e.target.checked)}
                                />
                            }
                            label="Force Processing (skip validation)"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleProcess} variant="contained" color="primary">
                    {dryRun ? 'Validate' : 'Process'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// New component for automated stats dialog
const AutomatedStatsDialog = ({ open, onClose, stats }) => {
    if (!stats) return null;
    
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Automated Withdrawal Processing Statistics</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                            <CardHeader title="Processing Summary" />
                            <CardContent>
                                <Typography variant="h6" color="primary">
                                    {stats.total_processed || 0}
                                </Typography>
                                <Typography variant="body2">Total Processed</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                            <CardHeader title="Success Rate" />
                            <CardContent>
                                <Typography variant="h6" color="success.main">
                                    {stats.success_rate ? `${(stats.success_rate * 100).toFixed(1)}%` : 'N/A'}
                                </Typography>
                                <Typography variant="body2">Successful Processing</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardHeader title="Recent Errors" />
                            <CardContent>
                                {stats.error_summary && stats.error_summary.length > 0 ? (
                                    <Box>
                                        {stats.error_summary.map((error, index) => (
                                            <Alert key={index} severity="error" sx={{ mb: 1 }}>
                                                <Typography variant="body2">
                                                    <strong>{error.error_type}:</strong> {error.message}
                                                </Typography>
                                                <Typography variant="caption">
                                                    Count: {error.count} | Last: {new Date(error.last_occurrence).toLocaleString()}
                                                </Typography>
                                            </Alert>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No recent errors
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

const rowsPerPage = 20;

export default function Withdrawals({ }) {
    const [usePagination, setUsePagination] = useState(true);
    const [statusQuery, setStatusQuery] = useState('all')
    const [page, setPage] = useState(0);
    const [briefSearchBox, setBriefSearchBox] = useState('');
    const [briefQuery, setBriefQuery] = useState('');
    const [pickedWithdrawal, setPickedWithdrawal] = useState(null);
    const [errorToast, setErrorToast] = useState(false);
    const [batchProcessingOpen, setBatchProcessingOpen] = useState(false);
    const [statsDialogOpen, setStatsDialogOpen] = useState(false);
    const [retryDialogOpen, setRetryDialogOpen] = useState(false);
    const [selectedWithdrawalForRetry, setSelectedWithdrawalForRetry] = useState(null);
    const [processAutomatedDialogOpen, setProcessAutomatedDialogOpen] = useState(false);
    const [selectedWithdrawalForProcessAutomated, setSelectedWithdrawalForProcessAutomated] = useState(null);

    // Creator payment dialog state
    const [isCreatorPaymentDialogOpen, setCreatorPaymentDialogOpen] = useState(false);
    const [selectedCreator, setSelectedCreator] = useState(null);
    
    const { dialogState, openDialog, closeDialog } = useAlertDialog();

    const { data, error, isFetching, refetch } = useQuery({
        queryKey: ['withdrawals', 'list', { page, statusQuery, briefQuery }],
        queryFn: () => API.withdrawals.adminList({
            page: usePagination ? page : undefined,
            rows: usePagination ? rowsPerPage : undefined,
            search_brief: !!briefQuery ? briefQuery : undefined,
            status: statusQuery
        }),
        refetchOnWindowFocus: false,
    });

    // Unified query for withdrawal stats
    const { data: withdrawalStats } = useQuery({
        queryKey: ['withdrawals', 'stats'],
        queryFn: () => API.withdrawals.getStats(),
        refetchOnWindowFocus: false,
        refetchInterval: 30000, // Refresh every 30 seconds
    });

    const { mutate, isLoading: updateLoading } = useMutation({
        mutationFn: ({ id, payload }) => API.withdrawals.adminUpdate(id, payload),
        onSuccess: () => {
            HandleCloseWithdrawal();
            openDialog('Success', 'Withdrawal updated succesfully');
            refetch();
        },
        onError: (updateError) => {
            openDialog('Error', <>Error while updating withdrawal<br />{updateError?.response?.data?.error || updateError?.message || ''}</>)
        }
    });

    // Unified mutations (like invoices)
    const { mutate: processWithdrawal, isLoading: processingWithdrawal } = useMutation({
        mutationFn: ({ id, options = {} }) => API.withdrawals.process(id, options),
        onSuccess: (data) => {
            openDialog('Success', `Withdrawal processing completed successfully`);
            refetch();
        },
        onError: (error) => {
            const errorMessage = error?.response?.data?.error || error?.message || '';
            const details = error?.response?.data?.details || '';
            const suggestion = error?.response?.data?.suggestion || '';
            openDialog('Error', <>
                Error processing withdrawal<br />
                {errorMessage}
                {details && <><br />Details: {details}</>}
                {suggestion && <><br />Suggestion: {suggestion}</>}
            </>)
        }
    });

    const { mutate: convertMode, isLoading: convertingMode } = useMutation({
        mutationFn: ({ id, mode, options = {} }) => API.withdrawals.convertMode(id, mode, options),
        onSuccess: (data) => {
            openDialog('Success', `Withdrawal successfully converted to ${data.mode || 'target'} mode`);
            refetch();
        },
        onError: (error) => {
            const errorMessage = error?.response?.data?.error || error?.message || '';
            const details = error?.response?.data?.details || '';
            const suggestion = error?.response?.data?.suggestion || '';
            openDialog('Error', <>
                Error converting withdrawal mode<br />
                {errorMessage}
                {details && <><br />Details: {details}</>}
                {suggestion && <><br />Suggestion: {suggestion}</>}
            </>)
        }
    });

    const { mutate: convertToAutomated, isLoading: convertingToAutomated } = useMutation({
        mutationFn: ({ id, options = {} }) => API.withdrawals.convertToAutomated(id, options),
        onSuccess: (data) => {
            openDialog('Success', `Withdrawal successfully converted to automated mode`);
            refetch();
        },
        onError: (error) => {
            const errorMessage = error?.response?.data?.error || error?.message || '';
            const details = error?.response?.data?.details || '';
            const suggestion = error?.response?.data?.suggestion || '';
            openDialog('Error', <>
                Error converting to automated mode<br />
                {errorMessage}
                {details && <><br />Details: {details}</>}
                {suggestion && <><br />Suggestion: {suggestion}</>}
            </>)
        }
    });

    const { mutate: convertToManual, isLoading: convertingToManual } = useMutation({
        mutationFn: ({ id, options = {} }) => API.withdrawals.convertToManual(id, options),
        onSuccess: (data) => {
            openDialog('Success', `Withdrawal successfully converted to manual mode`);
            refetch();
        },
        onError: (error) => {
            const errorMessage = error?.response?.data?.error || error?.message || '';
            const details = error?.response?.data?.details || '';
            const suggestion = error?.response?.data?.suggestion || '';
            openDialog('Error', <>
                Error converting to manual mode<br />
                {errorMessage}
                {details && <><br />Details: {details}</>}
                {suggestion && <><br />Suggestion: {suggestion}</>}
            </>)
        }
    });

    const { mutate: batchProcess, isLoading: batchProcessing } = useMutation({
        mutationFn: (options) => API.withdrawals.batchProcess(options),
        onSuccess: (data) => {
            const result = data.batch_processing_result || data;
            const processed = result.processed_count || result.successful || 0;
            const failed = result.failed_count || result.failed || 0;
            openDialog('Success', `Batch processing completed: ${processed} successful, ${failed} failed`);
            refetch();
        },
        onError: (error) => {
            openDialog('Error', <>Error in batch processing<br />{error?.response?.data?.error || error?.message || ''}</>)
        }
    });

    // Creator payment dialog handlers
    const handleCreatorPaymentDialogOpen = (creatorId, creatorName) => {
        setSelectedCreator({ id: creatorId, name: creatorName });
        setCreatorPaymentDialogOpen(true);
    };

    const handleCreatorPaymentDialogClose = () => {
        setCreatorPaymentDialogOpen(false);
        setSelectedCreator(null);
    };

    const OnUpdateWithdrawal = (payload) => {
        mutate({
            id: pickedWithdrawal,
            payload
        });
    }

    const handleProcessWithdrawal = (withdrawalId, options = {}) => {
        processWithdrawal({ id: withdrawalId, options });
    };

    const handleConvertMode = (withdrawal, targetMode) => {
        const options = {};
        if (targetMode === 'automated') {
            // Try to get payment service from withdrawal data
            const paymentService = withdrawal.payment_service || withdrawal.manual_service;
            if (paymentService) {
                options.payment_service = paymentService;
            }
        }
        convertMode({ id: withdrawal.id, mode: targetMode, options });
    };

    const handleConvertToAutomated = (withdrawal) => {
        const options = {};
        // Try to get payment service from withdrawal data
        const paymentService = withdrawal.payment_service || withdrawal.manual_service;
        if (paymentService) {
            options.payment_service = paymentService;
        }
        convertToAutomated({ id: withdrawal.id, options });
    };

    const handleConvertToManual = (withdrawal) => {
        convertToManual({ id: withdrawal.id });
    };

    const handleBatchProcess = (options) => {
        batchProcess(options);
    };

    const handleRefresh = (withdrawalId) => {
        // This would typically refresh the specific withdrawal status
        refetch();
    };

    useEffect(() => {
        if (error) {
            toast.error('Operation failed', {
                description: error.message
            });
        }
    }, [error])

    // Debounce search input
    useEffect(() => {
        const delayInputTimeoutId = setTimeout(() => {
            setBriefQuery(briefSearchBox);
        }, 1000);
        return () => clearTimeout(delayInputTimeoutId);
    }, [briefSearchBox]);

    // Reset page on filter change
    useEffect(() => {
        setPage(0);
    }, [briefQuery, usePagination, statusQuery]);

    const HandleClickWithdrawal = (id) => {
        setPickedWithdrawal(id);
    }

    const HandleCloseWithdrawal = () => {
        setPickedWithdrawal(null);
    }

    const handleRetryWithdrawal = (withdrawal) => {
        setSelectedWithdrawalForRetry(withdrawal);
        setRetryDialogOpen(true);
    }

    const handleRetryDialogClose = () => {
        setRetryDialogOpen(false);
        setSelectedWithdrawalForRetry(null);
    }

    const handleRetrySuccess = (response) => {
        toast.success('Withdrawal retry initiated successfully');
        refetch(); // Refresh the withdrawals list
    }

    // Process Automated Dialog Handlers
    const handleProcessApprovedManual = (withdrawal) => {
        setSelectedWithdrawalForProcessAutomated(withdrawal);
        setProcessAutomatedDialogOpen(true);
    }

    const handleProcessAutomatedDialogClose = () => {
        setProcessAutomatedDialogOpen(false);
        setSelectedWithdrawalForProcessAutomated(null);
    }

    return (
        <Box sx={{ padding: 2 }}>
            <AlertDialog alertState={dialogState}></AlertDialog>
            <AdminWithdrawalDialog 
                isOpen={!!pickedWithdrawal} 
                withdrawalId={pickedWithdrawal} 
                onCloseAction={HandleCloseWithdrawal} 
                onSaveForm={OnUpdateWithdrawal}
                onRetry={handleRetryWithdrawal}
            />
            <BatchProcessingDialog 
                open={batchProcessingOpen} 
                onClose={() => setBatchProcessingOpen(false)}
                onProcess={handleBatchProcess}
            />
            <AutomatedStatsDialog 
                open={statsDialogOpen} 
                onClose={() => setStatsDialogOpen(false)}
                stats={withdrawalStats}
            />
            
            {/* Creator Payment Dialog */}
            {isCreatorPaymentDialogOpen && selectedCreator && (
                <CreatorPaymentDialog
                    open={isCreatorPaymentDialogOpen}
                    onClose={handleCreatorPaymentDialogClose}
                    creatorId={selectedCreator.id}
                    creatorName={selectedCreator.name}
                />
            )}
            
            {/* Retry Withdrawal Dialog */}
            <RetryWithdrawalDialog
                open={retryDialogOpen}
                onClose={handleRetryDialogClose}
                withdrawalId={selectedWithdrawalForRetry?.id}
                withdrawalData={selectedWithdrawalForRetry}
                onRetrySuccess={handleRetrySuccess}
            />

            {/* Process Automated Dialog */}
            <ProcessAutomatedDialog
                open={processAutomatedDialogOpen}
                onClose={handleProcessAutomatedDialogClose}
                withdrawal={selectedWithdrawalForProcessAutomated}
            />

            <Backdrop sx={{ color: '#fff', zIndex: (theme) => !!pickedWithdrawal ? theme.zIndex.modal + 1 : theme.zIndex.drawer - 1 }} open={isFetching || processingWithdrawal || convertingMode || convertingToAutomated || convertingToManual || batchProcessing}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Snackbar
                open={errorToast}
                autoHideDuration={6000}
                onClose={() => setErrorToast(false)}
            >
                <Alert severity='error' variant='filled' sx={{ width: '100%' }}>
                    {error?.response?.data?.error || error?.message || ''}
                </Alert>
            </Snackbar>
            {/* Control Bar */}
            <Box sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
                mb: 3
            }}>
                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search..."
                    value={briefSearchBox}
                    onChange={(e) => setBriefSearchBox(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    select
                    value={statusQuery}
                    onChange={event => setStatusQuery(event.target.value)}
                    label='Status'
                    size="small"
                    sx={{ minWidth: '10rem' }}
                >
                    <MenuItem value="all">All Statuses</MenuItem>
                    {withdrawalStatuses.map((withdrawStatus) => <MenuItem key={withdrawStatus} value={withdrawStatus} sx={{ textTransform: 'capitalize' }}>{withdrawStatus}</MenuItem>)}
                </TextField>
                <FormControlLabel value={usePagination} onChange={(event, checked) => setUsePagination(checked)} label='Use Pagination?' control={<Switch></Switch>}>
                </FormControlLabel>

                {/* New automated processing controls */}
                <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                    <Button
                        variant="outlined"
                        startIcon={<ProcessIcon />}
                        onClick={() => setBatchProcessingOpen(true)}
                        disabled={batchProcessing}
                    >
                        Batch Process
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<StatsIcon />}
                        onClick={() => setStatsDialogOpen(true)}
                    >
                        Stats
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => refetch()}
                        disabled={isFetching}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>
            <Box sx={{ marginBlockStart: 1, marginInline: "auto" }}>
                <TableContainer 
                    component={Paper} 
                    elevation={1}
                    sx={{ 
                        maxWidth: '100%',
                        overflowX: 'auto'
                    }}
                >
                    <Table sx={{ minWidth: 1200 }}>
                        <TableHead>
                            <StyledTableRow>
                                <TableCell>
                                    Reference #
                                </TableCell>
                                <TableCell>
                                    Status
                                </TableCell>
                                <TableCell>
                                    Issued at
                                </TableCell>
                                <TableCell>
                                    Pay by
                                </TableCell>
                                <TableCell>
                                    Subtotal
                                </TableCell>
                                <TableCell>
                                    Fees %
                                </TableCell>
                                <TableCell>
                                    Total
                                </TableCell>
                                <TableCell>
                                    Subject Type
                                </TableCell>
                                <TableCell>
                                    Subject
                                </TableCell>
                                <TableCell>
                                    Subject Payment Platform
                                </TableCell>
                                <TableCell>
                                    Subject Payment Email
                                </TableCell>
                                <TableCell>
                                    Brief
                                </TableCell>
                                <TableCell>
                                    Notes
                                </TableCell>
                                <TableCell>
                                    Actions
                                </TableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {data?.withdrawals.map((wdw) => (
                                <WithdrawalRow
                                    key={wdw.id}
                                    wdw={wdw}
                                    onClickWithdrawal={HandleClickWithdrawal}
                                    onProcessAutomated={handleProcessWithdrawal}
                                    onConvertMode={handleConvertMode}
                                    onRetry={handleRetryWithdrawal}
                                    onProcessApprovedManual={handleProcessApprovedManual}
                                    onConvertToAutomated={handleConvertToAutomated}
                                    onConvertToManual={handleConvertToManual}
                                    onRefresh={handleRefresh}
                                    onPaymentInfoClick={handleCreatorPaymentDialogOpen}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {usePagination && <Box sx={{ paddingBlock: 2 }}>
                    <Pagination
                        sx={{ marginInline: 'auto', maxWidth: 'fit-content' }}
                        count={data ? Math.floor(data.total / rowsPerPage) : 0} page={page + 1}
                        onChange={(event, value) => setPage(value - 1)}>
                    </Pagination>
                </Box>}
            </Box>
        </Box>
    )
}